/** @format */

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, Popover } from "@headlessui/react";
import { Link, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useStableFeatureFlags } from "@/hooks/useStableFeatureFlags";
import { faBars } from "@/utils/iconLibraryTreeShake";
import { logout, useUser } from "../api/userAPI";
import logo from "../assets/logo.png";
import { LiveRaceBanner } from "../components/Header/LiveRaceBanner";
import { NotificationBell } from "../components/Header/NotificationBell";
import { SanctioningBodyNav } from "../components/Header/SanctioningBodyNav";
import { useCentrifugoNotifications } from "../hooks/useCentrifugoNotifications";
import Login from "../pages/Login";
import ResetPassword from "../pages/ResetPassword";
import SignUp from "../pages/SignUp";
import UserProfile from "../pages/UserProfile";
import LoadingCircle from "./LoadingCircle";

const DOCS_URL = "/docs/";

const NavLinks = ({ className }: { className?: string }) => (
  <nav className={className}>
    <a
      href="https://blog.galaxie.app/posts/"
      target="_blank"
      rel="noopener noreferrer"
      className="hover:text-white transition-colors"
    >
      Blog
    </a>
    <a
      href={DOCS_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="hover:text-white transition-colors"
    >
      Docs
    </a>
    <a href="mailto:contact@galaxie.app" className="hover:text-white transition-colors">
      Contact
    </a>
    {import.meta.env.DEV && (
      <Link
        to="/dev"
        className="text-amber-400/70 hover:text-amber-300 transition-colors"
      >
        Dev
      </Link>
    )}
  </nav>
);

const Header = () => {
  // check react router for widget page
  const widgetPage = window.location.pathname.split("/").includes("widget");
  const { data: user, status } = useUser();
  const flags = useStableFeatureFlags();
  const notificationsEnabled = flags?.includes("notifications") ?? false;
  const modal = useSearch({
    strict: false,
    select: search => (search as Record<string, unknown>).modal as string | undefined,
  });
  const [modalOpen, setModalOpen] = useState(!!modal);
  const [modalComponent, setModalComponent] = useState<string | null>(modal ?? "login");
  const [profileInitialTab, setProfileInitialTab] = useState<
    "profile" | "notifications"
  >("profile");

  useEffect(() => {
    if (modal) {
      setModalComponent(modal);
      setModalOpen(true);
    }
  }, [modal]);

  const openProfileNotifications = () => {
    setProfileInitialTab("notifications");
    setModalComponent("profile");
    setModalOpen(true);
  };

  // Initialize notification WebSocket
  useCentrifugoNotifications();

  return widgetPage ? null : (
    <header className="bg-slate-900 border-b border-slate-700 print:hidden">
      <div className="grid grid-cols-[1fr_auto] grid-rows-[auto_auto] md:grid-cols-[auto_1fr_auto] md:grid-rows-1 items-center px-2 py-1 gap-2">
        {/* Logo + Desktop Nav */}
        <div className="flex items-center gap-4 min-w-0 md:col-start-1 md:row-start-1">
          <Link
            to="/"
            className="block bg-black/70 rounded-xl py-1 px-2 shrink"
            aria-label="Go to home page"
          >
            <img
              src={logo}
              alt="logo"
              className="inline-block h-8 w-auto"
              width="413"
              height="413"
            />
            <span className="text-md md:text-2xl mx-2 align-bottom">Galaxie</span>
          </Link>

          {/* Desktop nav links */}
          <SanctioningBodyNav variant="desktop" />
          <NavLinks className="hidden md:flex items-center gap-4 text-sm text-slate-400" />
        </div>

        {/* Actions Section */}
        <div className="flex items-center justify-end gap-2 flex-nowrap md:col-start-3 md:row-start-1">
          {status === "pending" ? (
            <LoadingCircle className="h-5 w-5 animate-spin" />
          ) : (
            <>
              {user?.id ? (
                <>
                  {notificationsEnabled && (
                    <NotificationBell onPushEnabled={openProfileNotifications} />
                  )}

                  {/* User Menu */}
                  <Popover className="relative">
                    <Popover.Button
                      className="text-left border flex items-center rounded-lg border-slate-500 py-1.5 px-3 whitespace-nowrap"
                      aria-label="User menu"
                    >
                      <span>{user.shortName}</span>
                      <FontAwesomeIcon icon={faBars} className="-mr-1 ml-2" />
                    </Popover.Button>
                    <Popover.Panel
                      anchor={{ to: "bottom end", gap: 4, padding: 8 }}
                      className="z-30 rounded border border-slate-500 bg-slate-900 whitespace-nowrap text-right"
                    >
                      {/* Sanctioning body switcher (mobile only — desktop renders inline) */}
                      <SanctioningBodyNav variant="mobile" />
                      {/* Nav links visible only on mobile */}
                      <a
                        href="https://blog.galaxie.app/posts/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block md:hidden px-4 py-2 text-sm text-slate-200 hover:bg-slate-100 hover:text-slate-900"
                      >
                        Blog · Analysis
                      </a>
                      <a
                        href={DOCS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block md:hidden px-4 py-2 text-sm text-slate-200 hover:bg-slate-100 hover:text-slate-900"
                      >
                        Docs
                      </a>
                      <a
                        href="mailto:contact@galaxie.app"
                        className="block md:hidden px-4 py-2 text-sm text-slate-200 hover:bg-slate-100 hover:text-slate-900 border-b border-slate-700"
                      >
                        Contact
                      </a>
                      <button
                        type="button"
                        onClick={() => {
                          setProfileInitialTab("profile");
                          setModalOpen(true);
                          setModalComponent("profile");
                        }}
                        className="block px-4 py-2 w-full text-sm text-slate-200 hover:bg-slate-100 hover:text-slate-900 text-right"
                        aria-label="View user profile"
                      >
                        User Profile
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          void logout();
                        }}
                        className="block px-4 w-full py-2 text-sm text-slate-200 hover:bg-slate-100 hover:text-slate-900 text-right"
                        aria-label="Log out"
                      >
                        Log Out
                      </button>
                    </Popover.Panel>
                  </Popover>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setModalOpen(true);
                      setModalComponent("login");
                    }}
                    className="font-bold leading-none border rounded border-slate-500 px-2 py-1 bg-black/70 mr-1"
                    aria-label="Login"
                  >
                    Login
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setModalOpen(true);
                      setModalComponent("register");
                    }}
                    className="font-bold leading-none border rounded border-slate-500 px-2 py-1 bg-black/70"
                    aria-label="Sign Up"
                  >
                    Sign Up
                  </button>
                </>
              )}

              {/* Mobile hamburger for nav links (unauthenticated only — authenticated users get these in the user menu) */}
              <Popover className={`relative md:hidden ${user?.id ? "hidden" : ""}`}>
                <Popover.Button
                  className="border flex items-center rounded-lg border-slate-500 py-1.5 px-2"
                  aria-label="Navigation menu"
                >
                  <FontAwesomeIcon icon={faBars} />
                </Popover.Button>
                <Popover.Panel
                  anchor={{ to: "bottom end", gap: 4, padding: 8 }}
                  className="z-30 rounded border border-slate-500 bg-slate-900 whitespace-nowrap text-right min-w-[160px]"
                >
                  <SanctioningBodyNav variant="mobile" />
                  <a
                    href="https://blog.galaxie.app/posts/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-4 py-2 text-sm text-slate-200 hover:bg-slate-100 hover:text-slate-900"
                  >
                    Blog · Analysis
                  </a>
                  <a
                    href={DOCS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-4 py-2 text-sm text-slate-200 hover:bg-slate-100 hover:text-slate-900"
                  >
                    Docs
                  </a>
                  <a
                    href="mailto:contact@galaxie.app"
                    className="block px-4 py-2 text-sm text-slate-200 hover:bg-slate-100 hover:text-slate-900"
                  >
                    Contact
                  </a>
                </Popover.Panel>
              </Popover>

              <Dialog open={modalOpen} onClose={() => setModalOpen(false)}>
                <div className="fixed inset-0 backdrop-blur-sm" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                  <Dialog.Panel className="w-full max-w-2xl">
                    {modalComponent === "login" ? (
                      <Login
                        setModalComponent={setModalComponent}
                        setModalOpen={setModalOpen}
                      />
                    ) : modalComponent === "register" ? (
                      <SignUp
                        setModalComponent={setModalComponent}
                        setModalOpen={setModalOpen}
                      />
                    ) : modalComponent === "resetPassword" ? (
                      <ResetPassword
                        setModalComponent={setModalComponent}
                        setModalOpen={setModalOpen}
                      />
                    ) : modalComponent === "profile" ? (
                      <UserProfile
                        setModalComponent={setModalComponent}
                        setModalOpen={setModalOpen}
                        initialTab={profileInitialTab}
                      />
                    ) : null}
                  </Dialog.Panel>
                </div>
              </Dialog>
            </>
          )}
        </div>

        {/* Live Race Banner - Full width on mobile, centered on desktop */}
        <div className="col-span-2 md:col-span-1 md:col-start-2 md:row-start-1 flex items-center justify-center">
          <LiveRaceBanner />
        </div>
      </div>
    </header>
  );
};

export default Header;
